/**
 * Parse structured sections from IRL Find Evidence AI validation markdown.
 */

const EVIDENCE_ANSWER_SPLIT =
    /(?:^|\n)(?:#{1,3}\s*)?(?:\d+\.\s*)?Evidence[- ]based answer(?:\s+to\s+the\s+diligence\s+question)?[^\n]*\n/i;

const NOT_ANSWERABLE = /not answerable from retrieved vdr/i;

const OMISSIONS_SECTION_SPLIT =
    /(?:^|\n)(?:#{1,3}\s*)?(?:\d+\.\s*)?Management omissions(?:\s+from\s+VDR)?[^\n]*\n/i;

/**
 * @param {string} rawAnswer
 * @returns {{
 *   validationBody: string,
 *   evidenceBasedAnswer: string | null,
 *   hasEvidenceBasedAnswer: boolean,
 *   evidenceAnswerConfidence: 'high' | 'medium' | 'low' | null,
 *   managementOmissions: string[],
 * }}
 */
function parseManagementOmissionsSection(fullText, afterValidationBody) {
    const searchIn = afterValidationBody || fullText;
    const match = searchIn.match(OMISSIONS_SECTION_SPLIT);
    if (!match || match.index == null) return [];

    let section = searchIn.slice(match.index + match[0].length).trim();
    const nextHeading = section.search(/\n(?:#{1,3}\s*)?\d+\.\s+/);
    if (nextHeading > 0) section = section.slice(0, nextHeading).trim();

    if (/^none\b/i.test(section) || /none identified/i.test(section) || /none —/i.test(section)) {
        return [];
    }

    const items = [];
    for (const line of section.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed || !/^[-*]/.test(trimmed)) continue;
        let item = trimmed.replace(/^[-*]\s*/, '');
        item = item.replace(/^missing from management response:\s*/i, '');
        item = item.replace(/\s*\(see\s*\[[\d,\s]+\]\)\s*$/i, '');
        item = item.trim();
        if (item && !/^none\b/i.test(item)) items.push(item);
    }
    return items;
}

export function parseIrlValidationAnswer(rawAnswer) {
    const text = String(rawAnswer || '').trim();
    if (!text) {
        return {
            validationBody: '',
            evidenceBasedAnswer: null,
            hasEvidenceBasedAnswer: false,
            evidenceAnswerConfidence: null,
            managementOmissions: [],
        };
    }

    const splitMatch = text.match(EVIDENCE_ANSWER_SPLIT);
    if (!splitMatch || splitMatch.index == null) {
        return {
            validationBody: text,
            evidenceBasedAnswer: null,
            hasEvidenceBasedAnswer: false,
            evidenceAnswerConfidence: null,
            managementOmissions: parseManagementOmissionsSection(text),
        };
    }

    const splitAt = splitMatch.index;
    const validationBody = text.slice(0, splitAt).trim();
    let evidenceBasedAnswer = text.slice(splitAt + splitMatch[0].length).trim();

    const nextHeading = evidenceBasedAnswer.search(/\n(?:#{1,3}\s*)?\d+\.\s+/);
    if (nextHeading > 0) {
        evidenceBasedAnswer = evidenceBasedAnswer.slice(0, nextHeading).trim();
    }

    const hasEvidenceBasedAnswer =
        evidenceBasedAnswer.length > 20 && !NOT_ANSWERABLE.test(evidenceBasedAnswer);

    let evidenceAnswerConfidence = null;
    const confMatch = evidenceBasedAnswer.match(/\bconfidence:\s*(high|medium|low)\b/i);
    if (confMatch) {
        evidenceAnswerConfidence = confMatch[1].toLowerCase();
    }

    return {
        validationBody,
        evidenceBasedAnswer: hasEvidenceBasedAnswer ? evidenceBasedAnswer : null,
        hasEvidenceBasedAnswer,
        evidenceAnswerConfidence,
        managementOmissions: parseManagementOmissionsSection(text, validationBody),
    };
}
